import React, { useCallback, useEffect, useState } from 'react';
import { FiCheckSquare } from 'react-icons/fi';
import api, { clientStepsAPI } from '../services/api';
import { LEAD_SOURCES, paidValue, paidFromValue } from './shared/leadSources';

// Staff-only ticks on a client's file (founder, 22 Oct 2026): the tourist trip checklist and
// the internal steps the client never sees, plus where the client came from (KPI CMO3).
// Templates are edited under /api/client-step-templates/ (Admin-level staff).

const ENDPOINT = { STUDENT: 'students', JOB: 'job-clients', TOURIST: 'tourist-clients', MEDICAL: 'medical-clients' };

const box = { border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', margin: '12px 0', background: '#fff' };
const sel = { border: '1px solid #cbd5e1', borderRadius: 8, padding: '5px 8px', fontSize: 13, background: '#fff' };

const ClientStepsPanel = ({ kind, clientId, leadSource, leadPaid }) => {
  const [sections, setSections] = useState(null);
  const [source, setSource] = useState({ lead_source: leadSource || '', lead_paid: leadPaid ?? null });
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    clientStepsAPI.get(kind, clientId).then(({ data }) => setSections(data.sections)).catch(() => setSections([]));
  }, [kind, clientId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSource({ lead_source: leadSource || '', lead_paid: leadPaid ?? null }); }, [leadSource, leadPaid]);

  const tick = async (template, done) => {
    try {
      const { data } = await clientStepsAPI.tick(kind, clientId, template, done);
      setSections(data.sections);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Could not save the tick.');
    }
  };

  const saveSource = async (patch) => {
    const next = { ...source, ...patch };
    setSource(next);
    try {
      await api.patch(`/${ENDPOINT[kind]}/${clientId}/`, { lead_source: next.lead_source, lead_paid: next.lead_paid });
      setMsg('Source saved.');
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Could not save the source.');
    }
  };

  return (
    <div style={box}>
      <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
        <FiCheckSquare color="#011ff3" /> Staff checklist
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', margin: '8px 0', fontSize: 13 }}>
        <span>Came from</span>
        <select style={sel} value={source.lead_source} onChange={(e) => saveSource({ lead_source: e.target.value })}>
          <option value="">Not recorded</option>
          {LEAD_SOURCES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select style={sel} value={paidValue(source.lead_paid)} onChange={(e) => saveSource({ lead_paid: paidFromValue(e.target.value) })}>
          <option value="">Paid or organic?</option>
          <option value="PAID">Paid ad</option>
          <option value="ORGANIC">Organic</option>
        </select>
      </div>
      {sections === null && <div style={{ color: '#94a3b8', fontSize: 12.5 }}>Loading…</div>}
      {sections && sections.map((s) => (
        <div key={s.section} style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>{s.label} · {s.done}/{s.total}</div>
          {s.items.map((i) => (
            <label key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '4px 0', color: i.done ? '#047857' : '#1e293b', cursor: 'pointer' }}>
              <input type="checkbox" checked={i.done} onChange={(e) => tick(i.id, e.target.checked)} />
              {i.label}
              {i.done && <span style={{ color: '#94a3b8', fontSize: 11.5 }}>· {i.done_by} {i.done_at ? new Date(i.done_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}</span>}
            </label>
          ))}
        </div>
      ))}
      {msg && <div style={{ fontSize: 12, color: msg.startsWith('Could') ? '#b91c1c' : '#047857', marginTop: 6 }}>{msg}</div>}
    </div>
  );
};

export default ClientStepsPanel;
